import Big from 'big.js';
import * as models from 'smg/customer-api/models';
import * as smgPromo from 'smg/customer-api/promos';
import { Session } from 'smg/customer-api/session';
import * as blockchyp from 'smg/blockchyp';
import * as template from 'smg/template';
import { GetFirstAndLast } from 'smg/users';

import { Dom, QueryDom } from './dom';
import { AdditionalDetails, CheckoutPhase, Payment, Split } from './types';

type sessionResp = Awaited<ReturnType<typeof Session>>;

// State holds every piece of mutable state the checkout page shares across
// sections, plus function slots that sections populate during setup so they
// can call each other without importing each other directly.
export interface State {
    sess?: sessionResp;
    getUser: () => Promise<models.User>;

    userShippingOptions: Map<string, models.HyejooShippingOption>;
    userTaxExemptions: models.UserTaxExemption[];
    userPaymentOptions: Set<models.PaymentOption>;

    cartID?: string;
    cartItems: models.CartItem[];
    customer?: models.User;
    shippingOption?: models.ShippingOption;
    taxExemption?: models.UserTaxExemption;
    taxExemptionUploadThumbnails: HTMLElement[];
    payment: Payment;
    // Set while the customer is spreading the order across two or more cards.
    // Absent for a one-method payment. See Payment in ./types.ts.
    split?: Split;
    // The customer can change the checkout until Place Order or the split switch takes it over.
    phase: CheckoutPhase;
    // The cart poller reloads the page when the cart changes. Deleting the cart at completion is such a change,
    // so completing the order aborts it first.
    cartReloader?: AbortController;
    // Ids of cards this checkout created. The default label is written for one of them after the charges succeed.
    createdThisCheckout: Set<string>;
    // The single-card charge's idempotency key. It lives on the state so it survives the
    // create-order-payments thunk being rebuilt on Retry Payment, and is reused until a charge
    // succeeds, so a lost response does not double-charge (#5656).
    orderPaymentIdemKey?: string;
    // When a resumed order already has payments, the order total shown is
    // those payments plus resumableOrder.balance, not the cart's total.
    resumeAuthoritativeTotal?: Big;
    // Additional details are on the order already (resume) or the customer
    // confirmed them. A split can then charge from the summary.
    detailsComplete: boolean;
    // Active BlockChyp iframe mount for the in-progress credit-card form (new
    // or edit). PAN/CVC live inside this cross-origin iframe; CreatePaymentOption
    // / replacePaymentOption tokenize it instead of reading raw inputs. Unloaded
    // and cleared once the payment option is created.
    creditCardMount?: blockchyp.Mount;
    // Cached webPaymentsToken resolution shared by every BlockChyp mount this
    // checkout builds. See ResolveMerchantToken in ./payment/merchant.ts.
    merchantToken?: Promise<string>;
    additionalDetails: AdditionalDetails;
    orderResp?: models.OrderCreateResponse;
    promo?: models.PromoCodeResult;

    promoDiscountAmount: Big;
    availableDiscountAmount: Big;
    subtotalAmount: Big;
    taxAmount: Big;
    shippingAmount: Big;
    totalAmount: Big;
    taxRate: Big;
    // Project deposit credit from the cart response, server-capped at the
    // pre-prepayment total. The order create applies it automatically at
    // place-order; checkout only displays it so the totals add up.
    prepaymentAmount: Big;
    pendingDepositOrders: models.CartPendingDepositOrder[];

    taxFirstOpen: boolean;
    taxStepComplete: boolean;
    paymentStepComplete: boolean;

    dom: Dom;
    promoInput: smgPromo.Input;

    startLoading: () => void;
    stopLoading: () => void;

    // Section callables — populated by each section's setup() during init.
    updateTotals: () => Promise<void>;
    updateOrderSummary: () => Promise<void>;
    updateShippingSummary: () => void;
    updateTaxExemptionSummary: () => Promise<void>;
    updatePaymentSummary: () => void;
    syncSplitSummary: () => void;
    updateAdditionalDetailsSummary: () => void;

    initShipping: () => Promise<void>;
    initTax: () => Promise<void>;
    initPayment: () => Promise<void>;
    initAdditional: () => void;

    promptShippingOption: (shippingOptionInit?: models.IShippingOption) => void;
    promptTaxExemption: (uteInit?: models.IUserTaxExemption) => void;
    promptNewPaymentOption: ($el?: HTMLElement, returnTo?: () => void) => void;
    promptExistingPaymentOption: ($el?: HTMLElement, returnTo?: () => void) => void;
    promptNewPaymentOptionByType: (
        typeID: models.PaymentOptionTypeID,
        $el?: HTMLElement,
        returnTo?: () => void,
        uploadIDs?: number[],
    ) => void;
    editSavedPaymentOption: (poID: string, returnTo?: () => void) => void;
    deleteSavedPaymentOption: (poID: string) => Promise<void>;

    initPaymentTypeSelect: (typeID: models.PaymentOptionTypeID, $el?: HTMLElement) => void;
    initCCInputs: ($el?: HTMLElement) => void;

    createPaymentOption: () => Promise<models.PaymentOption>;
    getPaymentOptions: (page?: number) => Promise<models.PaymentOptionsHyejooResponse>;
    updateTaxExemptionOptions: (userID: string) => Promise<void>;
    checkTaxSelected: () => void;

    submitOrder: () => Promise<void>;
}

function unset<T>(name: string): T {
    return (() => {
        throw new Error(`${name} not wired yet`);
    }) as unknown as T;
}

// CreateState resolves the DOM, the session, and the promo input, and hands
// back a State object with data fields populated and callable slots stubbed.
// Sections fill the callable slots during their setup() pass.
export async function CreateState(): Promise<State> {
    // The loading spinner lives in a <template> in the payment shortcode; clone
    // it into the body before QueryDom so #loading-spinner can be found.
    document.body.appendChild(template.Clone('#loading-spinner-template'));

    const dom = QueryDom();
    const sess = await Session().catch(() => undefined);

    const getUser: State['getUser'] = async () =>
        await Session().then(s => s.user).catch(() => {
            const shippingAddress = state.shippingOption?.address;
            const { firstName, lastName } = GetFirstAndLast(shippingAddress?.fullName || '');
            return new models.User({
                firstName,
                lastName,
                company: shippingAddress?.organization || '',
                email: shippingAddress?.emailAddress?.email || '',
                phone: shippingAddress?.phoneNumber?.phone || '',
                countryID: shippingAddress?.countryID,
                stateID: shippingAddress?.stateID,
            });
        });

    // The full-page loading wash is reference-counted with a short linger
    // before hiding: checkout boot and section transitions run several
    // await-separated start/stop pairs back-to-back (shipping list, tax,
    // payment, resume), and hiding between consecutive windows strobes the
    // rgba backdrop over the whole page. The linger merges windows up to
    // 300ms apart into one steady overlay; it's pointer-events: none, so
    // lingering never blocks input. Extra stops clamp at zero.
    let loadingCount = 0;
    let loadingHideTimer: number | undefined;

    const state: State = {
        sess,
        getUser,

        userShippingOptions: new Map<string, models.HyejooShippingOption>(),
        userTaxExemptions: [],
        userPaymentOptions: new Set<models.PaymentOption>(),

        cartItems: [],
        taxExemptionUploadThumbnails: [],
        payment: { billingShippingSame: true },
        phase: 'editing',
        createdThisCheckout: new Set<string>(),
        detailsComplete: false,
        additionalDetails: {
            marketingPermission: false,
            smsOptIn: false,
            verifiedDesigns: false,
        },

        promoDiscountAmount: Big(0),
        availableDiscountAmount: Big(0),
        subtotalAmount: Big(0),
        taxAmount: Big(0),
        shippingAmount: Big(0),
        totalAmount: Big(0),
        taxRate: Big(0),
        prepaymentAmount: Big(0),
        pendingDepositOrders: [],

        taxFirstOpen: true,
        taxStepComplete: false,
        paymentStepComplete: false,

        dom,
        promoInput: undefined as unknown as smgPromo.Input,

        startLoading: () => {
            loadingCount++;
            window.clearTimeout(loadingHideTimer);
            loadingHideTimer = undefined;
            dom.$loadingSpinner?.classList.remove('d-none');
        },
        stopLoading: () => {
            loadingCount = Math.max(0, loadingCount - 1);
            if (loadingCount > 0) {
                return;
            }
            window.clearTimeout(loadingHideTimer);
            loadingHideTimer = window.setTimeout(() => {
                if (loadingCount === 0) {
                    dom.$loadingSpinner?.classList.add('d-none');
                }
            }, 300);
        },

        updateTotals: unset('updateTotals'),
        updateOrderSummary: unset('updateOrderSummary'),
        updateShippingSummary: unset('updateShippingSummary'),
        updateTaxExemptionSummary: unset('updateTaxExemptionSummary'),
        updatePaymentSummary: unset('updatePaymentSummary'),
        syncSplitSummary: () => undefined,
        updateAdditionalDetailsSummary: unset('updateAdditionalDetailsSummary'),

        initShipping: unset('initShipping'),
        initTax: unset('initTax'),
        initPayment: unset('initPayment'),
        initAdditional: unset('initAdditional'),

        promptShippingOption: unset('promptShippingOption'),
        promptTaxExemption: unset('promptTaxExemption'),
        promptNewPaymentOption: unset('promptNewPaymentOption'),
        promptExistingPaymentOption: unset('promptExistingPaymentOption'),
        promptNewPaymentOptionByType: unset('promptNewPaymentOptionByType'),
        editSavedPaymentOption: unset('editSavedPaymentOption'),
        deleteSavedPaymentOption: unset('deleteSavedPaymentOption'),

        initPaymentTypeSelect: unset('initPaymentTypeSelect'),
        initCCInputs: unset('initCCInputs'),

        createPaymentOption: unset('createPaymentOption'),
        getPaymentOptions: unset('getPaymentOptions'),
        updateTaxExemptionOptions: unset('updateTaxExemptionOptions'),
        checkTaxSelected: unset('checkTaxSelected'),

        submitOrder: unset('submitOrder'),
    };

    return state;
}
